import React, { useMemo } from 'react';
import styles from './DateInput.module.scss';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const calendarIcon = (
  <svg
    width='22'
    height='22'
    viewBox='0 0 22 22'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <g id='Group 20956'>
      <path
        id='Vector'
        d='M5.43428 0C5.89173 0 6.26256 0.356629 6.26256 0.79655V2.40335C6.99725 2.38964 7.82078 2.38964 8.74737 2.38964H13.1648C14.0914 2.38964 14.9149 2.38964 15.6496 2.40335V0.79655C15.6496 0.356629 16.0204 0 16.4779 0C16.9353 0 17.3062 0.356629 17.3062 0.79655V2.47382C18.8957 2.59623 19.9393 2.89665 20.7059 3.63393C21.4726 4.37121 21.785 5.37475 21.9122 6.90341L21.9999 7.69996H1.01701H0V6.90341C0.12728 5.37475 0.439668 4.37121 1.20631 3.63393C1.97296 2.89665 3.01647 2.59623 4.60601 2.47382V0.79655C4.60601 0.356629 4.97684 0 5.43428 0Z'
        fill='#A9A7A7'
      />
      <path
        id='Vector_2'
        d='M22 14.0001V12.0001C22 11.161 21.9965 9.66526 21.9823 9H0.0110106C-0.00319018 9.66526 0.000406649 11.161 0.000406649 12.0001V14.0001C0.000406649 17.7712 0.000406628 19.6568 1.28911 20.8284C2.57782 22 4.65196 22 8.80026 22H13.2002C17.3485 22 19.4226 22 20.7114 20.8284C22 19.6568 22 17.7712 22 14.0001Z'
        fill='#A9A7A7'
      />
    </g>
  </svg>
);

const pad = (n) => String(n).padStart(2, '0');

const formatDateOnlyLocal = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const formatDateTimeLocal = (d) =>
  `${formatDateOnlyLocal(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
    d.getSeconds()
  )}`;

const parseLocalYYYYMMDD = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const d = new Date(y, mo - 1, da, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return null;

    const localDate = parseLocalYYYYMMDD(s);
    if (localDate) return localDate;

    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  return null;
};

const DateInput = ({
  label,
  placeholder,
  value,
  onChange,
  name,
  errors,
  touched,
  showIcon,
  dateFormat,
  timeFormat,
  timeInputLabel = 'Time',
  showTimeSelect = false,
  showTimeInput = false,
  timeIntervals = 30,
  use24Hour = true,
  right,
}) => {
  const wantsTime = showTimeSelect || showTimeInput;

  const selectedDate = useMemo(() => toDate(value), [value]);
  const isValuePresent = !!selectedDate;

  const _timeFormat = timeFormat || (use24Hour ? 'HH:mm' : 'h:mm aa');
  const _dateFormat =
    dateFormat ||
    (wantsTime
      ? use24Hour
        ? 'dd/MM/yyyy HH:mm'
        : 'dd/MM/yyyy h:mm aa'
      : 'dd/MM/yyyy');

  return (
    <div
      className={styles['add-icon-container']}
      style={{ direction: right ? 'rtl' : undefined }}
    >
      <label>{label}</label>

      <DatePicker
        showIcon={showIcon}
        icon={calendarIcon}
        calendarIconClassname={styles['icon']}
        selected={selectedDate}
        onChange={(date) => {
          if (!date) return onChange(name, '');

          const out = wantsTime
            ? formatDateTimeLocal(date)
            : formatDateOnlyLocal(date);
          onChange(name, out);
        }}
        placeholderText={placeholder}
        className={`${styles['date-input']} ${
          errors && touched ? styles['date-input-error'] : ''
        } ${isValuePresent ? styles['date-input-filled'] : ''}`}
        dateFormat={_dateFormat}
        timeFormat={wantsTime ? _timeFormat : undefined}
        timeInputLabel={timeInputLabel}
        showTimeSelect={showTimeSelect}
        showTimeInput={showTimeInput}
        timeIntervals={showTimeSelect ? timeIntervals : undefined}
        timeCaption='Time'
      />
    </div>
  );
};

export default DateInput;
